import type { ReactNode } from 'react'
import { Pressable, StyleSheet } from 'react-native'

import { FigmaChevronLeftIcon, FigmaCloseIcon } from '../icons/FigmaSystemIcons'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'

const ICON_SIZE = 20

export interface TopBarProps {
  title: string
  /** Pops the screen. Omit at the root of a flow. */
  onBack?: () => void
  /** Ends the flow. Omit where this screen can't be abandoned. */
  onClose?: () => void
}

/**
 * The sheet's header: back, title, close.
 */
export function TopBar({ title, onBack, onClose }: TopBarProps) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()

  return (
    <Box flexDirection="row" alignItems="center" style={styles.bar}>
      <IconSlot
        onPress={onBack}
        label={t('common.back')}
        icon={
          <FigmaChevronLeftIcon
            size={ICON_SIZE}
            color={theme.content.button.icon.default}
          />
        }
      />
      <Text
        variant="headerTitle"
        numberOfLines={1}
        // The design's line box, so the title's place in the row doesn't move
        // with the font-size token or the platform's own leading.
        style={[
          styles.title,
          { lineHeight: theme.textVariants.bodyMedium.lineHeight },
        ]}
      >
        {title}
      </Text>
      <IconSlot
        onPress={onClose}
        label={t('common.close')}
        icon={
          <FigmaCloseIcon
            size={ICON_SIZE}
            color={theme.content.button.icon.default}
          />
        }
      />
    </Box>
  )
}

/** One 32-square of the bar, empty when the host gave it nothing to do. */
function IconSlot({
  onPress,
  label,
  icon,
}: {
  onPress?: () => void
  label: string
  icon: ReactNode
}) {
  if (!onPress) {
    return <Box style={styles.slot} />
  }
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      // Takes the 32-square up to the 44 a finger needs.
      hitSlop={6}
      style={styles.slot}
    >
      {icon}
    </Pressable>
  )
}

const styles = StyleSheet.create({
  bar: { paddingTop: 20, paddingBottom: 12, paddingHorizontal: 12, gap: 4 },
  slot: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: { flex: 1 },
})
